import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Outlet, Route, Routes, useLocation } from "react-router";
import { NavigateWithQueryParams } from "../NavigateWithQueryParams";
import { useIndexedDb } from "../shared/storage/IndexedDbProvider";
import { DataFieldType, RegistrationFormResource } from "../shared/types";
import { RegistrationFormProvider } from "./provider/RegistrationFormProvider";
import { FilledSteps, useRegistrationForm } from "./provider/registrationFormProviderContext";
import { getDataFieldsFromForm } from "./registrationFormUtils";
import { RegistrationStepView } from "./RegistrationStepView";
import {
	RegistrationCancelPage,
	RegistrationCancelledPage,
	RegistrationDeclinePage,
	RegistrationDeclinedPage,
	RegistrationEditedPage,
	RegistrationLandingPage,
	RegistrationNominateReplacementPage,
	RegistrationReplacedPage,
	RegistrationSuccessPage,
	RegistrationSummaryPage,
	RegistrationWaitingListPage,
} from "./staticPages";
import { RegistrationScreeningConfirmedPage } from "./staticPages/RegistrationScreeningConfirmedPage";

interface Props {
	registrationForm: RegistrationFormResource;
}

export const FILLED_STEPS = "filledSteps";

export const RegistrationFormAvailablePage: FC<Props> = ({ registrationForm }) => {
	const [initialFilledSteps, setInitialFilledSteps] = useState<FilledSteps>({});
	const [isInitialized, setIsInitialized] = useState(false);
	const { get, clearObjectStore } = useIndexedDb();
	const isRegistered = registrationForm.guest.isRegistered;

	const hasFileField = useMemo(
		() => getDataFieldsFromForm(registrationForm).some((dataField) => dataField.type === DataFieldType.FILE),
		[registrationForm],
	);

	const initializeRegistration = useCallback(async () => {
		const sessionStoredData = sessionStorage.getItem(FILLED_STEPS);

		if (!sessionStoredData) {
			if (hasFileField) {
				await clearObjectStore("guest_files");
			}
			setIsInitialized(true);
			return;
		}

		const storedFilledSteps = JSON.parse(sessionStoredData) as FilledSteps;
		const dataFields = getDataFieldsFromForm(registrationForm);

		for (const filledStepId of Object.keys(storedFilledSteps)) {
			for (const dataFieldId of Object.keys(storedFilledSteps[+filledStepId])) {
				const dataField = dataFields.find((dataField) => `${dataField.id}` == dataFieldId);

				if (!dataField || dataField.type !== DataFieldType.FILE) {
					continue;
				}

				const valueId = storedFilledSteps[+filledStepId][dataFieldId];
				try {
					storedFilledSteps[+filledStepId][dataFieldId] = await get("guest_files", valueId);
				} catch {
					storedFilledSteps[+filledStepId][dataFieldId] = null;
				}
			}
		}

		setInitialFilledSteps(storedFilledSteps);
		setIsInitialized(true);
	}, [clearObjectStore, get, hasFileField, registrationForm]);

	useEffect(() => {
		void initializeRegistration();
	}, [initializeRegistration]);

	return (
		isInitialized && (
			<RegistrationFormProvider
				initialFilledSteps={initialFilledSteps}
				registrationForm={registrationForm}
				hasFileField={hasFileField}
			>
				<Routes>
					<Route element={<LandingPageChecker />}>
						<Route index element={<NavigateWithQueryParams to="landing-page" replace={true} />} />
						<Route path="landing-page" element={<RegistrationLandingPage />} />
						<Route path="summary" element={<RegistrationSummaryPage />} />
						<Route path="success" element={<RegistrationSuccessPage />} />
						<Route path="edited" element={<RegistrationEditedPage />} />
						<Route path="decline" element={<RegistrationDeclinePage />} />
						<Route path="declined" element={<RegistrationDeclinedPage />} />
						<Route path="nominate-replacement" element={<RegistrationNominateReplacementPage />} />
						<Route path="replaced" element={<RegistrationReplacedPage />} />
						<Route path="cancel" element={<RegistrationCancelPage />} />
						<Route path="cancelled" element={<RegistrationCancelledPage />} />
						<Route path="confirmed" element={<RegistrationScreeningConfirmedPage />} />
						<Route path="waiting-list" element={<RegistrationWaitingListPage />} />
						{(registrationForm.isEditable || !isRegistered) && (
							<Route
								path=":stepId"
								element={<RegistrationStepView travelSectionSettings={registrationForm.travelSectionSettings} />}
							/>
						)}
					</Route>
				</Routes>
			</RegistrationFormProvider>
		)
	);
};

function LandingPageChecker() {
	const { pathname } = useLocation();
	const { registrationForm, filledSteps, hasSeenLanding, landingConsents, areConsentsLoading } = useRegistrationForm();
	const shouldShowLandingPage = registrationForm.landingBlockId !== null || landingConsents.length;

	if (areConsentsLoading) {
		return null;
	}

	if (
		shouldShowLandingPage &&
		Object.keys(filledSteps).length === 0 &&
		!pathname.endsWith("/landing-page") &&
		!hasSeenLanding
	) {
		return <NavigateWithQueryParams to={`/registrations/${registrationForm.id}`} replace={true} />;
	}

	return <Outlet />;
}
